import styled from '@emotion/styled'
import {
  ExtranetCustomerUserProfessionalRoleDto,
  ExtranetProductProfessionalRoleDto,
  ExtranetPurchaseDto,
  ExtranetShopProductDto,
} from '@pharmac-extranet/extranet-api'
import {
  BreakpointWidths,
  ExtranetContainer,
  MediaQuery,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacCheckbox,
  PharmacH4,
  PharmacH6,
  PharmacIcon,
  PharmacLink,
  PharmacSuccessToast,
  PharmacText,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { __ORDER_AND_DELIVERY_TERMS_LINK } from '../../../constants'
import { useAuthenticationContext } from '../../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../../router/useExtranetPath'
import { api } from '../../../services/HttpService'
import { ConfirmationModal } from '../../confirmation-modal/ConfirmationModal'
import { useShoppingCartContext } from './ShoppingCartContext'
import { ShoppingCartModalContent } from './ShoppingCartModalContent'

const ShoppingCartRoot = styled.div`
  position: relative;
  margin-left: auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: ${BreakpointWidths.Desktop - 1}px) {
    width: 38px;
  }
`

const EmptyShoppingCart = styled.div`
  position: absolute;
  top: calc(100%);
  z-index: 10;
  background-color: var(--color-white);
  background: var(--extranet-menu-bg-gradient);
  border-radius: var(--border-radius-small);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${MediaQuery.minMobile} {
    width: 90vw;
    right: -30px;
  }

  transition: opacity 0.3s var(--ease-cubic-bezier);
`

const EmptyShoppingCartInner = styled.div`
  min-width: 275px;
  padding-bottom: var(--spacing-small);

  @media (max-width: ${BreakpointWidths.Desktop - 1}px) {
    padding-top: var(--spacing-medium);
  }
`

const ShoppingCartClickLayer = ({ closeMenu }: { closeMenu: () => void }) => {
  return (
    <div
      onClick={closeMenu}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        width: '100%',
        height: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9,
      }}
    />
  )
}

export const ShoppingCartButton = () => {
  const hook = useShoppingCartContext()
  const { t } = useLanguage()
  const { minDesktop } = useBreakpoint()
  const navigate = useNavigate()
  const { customer } = useCustomerContext()
  const toastHook = usePharmacToast()
  const queryClient = useQueryClient()
  const [orderConsent, setOrderConsent] = useState<boolean>(false)
  const [openEmptyShoppingCart, setOpenEmptyShoppingCart] = useState(false)
  const [openShoppingCart, setOpenShoppingCart] = useState<boolean>(false)
  const createLink = useExtranetPath()
  const [productList, setProductList] = useState<ExtranetShopProductDto[]>([])
  const { user } = useAuthenticationContext()

  type T = {
    productId: number
    quantity: number
  }

  const formhook = useForm({
    mode: 'onChange',
  })

  const { mutate: purchaseProducts } = useMutation({
    mutationFn: (dto: ExtranetPurchaseDto) => api.shop.purchaseProduct(Number(customer?.id), dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>{t('extranet:shop.cart.orderSendSuccess')}</PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: ['__shopProducts'],
      })
      window.location.reload()
    },
  })

  const inCart: T[] = [...hook.selectedProductList]

  const productsInShoppingCart = inCart.map((t) => {
    const product = productList.find((a) => a?.productId === t.productId)

    const { watch } = formhook
    const productQuantity = watch(String(product?.productId))

    const hasRequiredRoles = (
      productRoles?: ExtranetProductProfessionalRoleDto[],
      userRoles?: ExtranetCustomerUserProfessionalRoleDto[]
    ) => {
      if (product?.professionalRoles && product?.professionalRoles.length > 0)
        return productRoles?.some((pr) =>
          userRoles?.find((ur) => pr.displayName === ur.displayName.fi)
        )
      return true
    }

    const decimalVat = Number(product?.taxRate?.percentage) / 100
    const priceWithVAT =
      Number(product?.unitSellingPrice) * decimalVat + Number(product?.unitSellingPrice)

    return {
      ...t,
      title: product?.title,
      unitSellingPrice: product?.unitSellingPrice,
      totalPriceWithoutVAT: Number(product?.unitSellingPrice) * t.quantity,
      VAT: Number(product?.unitSellingPrice) * t.quantity * decimalVat,
      taxPercentage: Number(product?.taxRate?.percentage),
      totalPriceWithVAT: priceWithVAT * t.quantity,
      hasroles: hasRequiredRoles(product?.professionalRoles, user?.professionalRoles),
      quantity: productQuantity,
    }
  })

  const quantityIsNotEmpty =
    productsInShoppingCart.filter((a) => a.quantity).length === productsInShoppingCart.length
  const quantityIsNotNegative = productsInShoppingCart.every((a) => Number(a.quantity) > 0)
  const quantityIsValid = formhook.formState.isValid

  const handleSubmitOrder = () => {
    const products = productsInShoppingCart.filter((i) =>
      Object.entries([i.productId, i.quantity]).map(([key, value]) => {
        if (value !== undefined && Number(value) > 0) {
          return { productId: Number(key), quantity: Number(value) }
        }
      })
    )

    purchaseProducts({
      items: products,
    })

    setOpenShoppingCart(false)
  }

  return (
    <ShoppingCartRoot>
      <ExtranetContainer
        marginMediumRight
        alignCenter
      >
        <PharmacButton
          onClick={
            hook.selectedProductList && hook.selectedProductList?.length > 0
              ? () => setOpenShoppingCart(true)
              : () => setOpenEmptyShoppingCart(!openEmptyShoppingCart)
          }
        >
          <PharmacIcon
            icon="shoppingCart"
            size="20px"
          />
          {hook.selectedProductList && hook.selectedProductList?.length > 0 && (
            <PharmacText
              paddingSmallLeft
              weight="bold"
            >
              {hook.selectedProductList?.length}
            </PharmacText>
          )}
        </PharmacButton>
      </ExtranetContainer>
      {hook.selectedProductList && hook.selectedProductList?.length > 0 && (
        <ConfirmationModal
          wideModal
          showOnProp={openShoppingCart}
          title={
            <ExtranetContainer
              full
              justifyCenter
            >
              <PharmacH4 color="purple">
                {t('extranet:physicalProducts.shoppingCart.title')}
              </PharmacH4>
            </ExtranetContainer>
          }
          onSubmit={() => handleSubmitOrder()}
          disableOnSubmit={
            !orderConsent || !quantityIsNotEmpty || !quantityIsNotNegative || !quantityIsValid
          }
          onClose={() => setOpenShoppingCart(false)}
          closeButtonText={t('common:cancel')}
          submitButtonText={t('extranet:physicalProducts.shoppingCart.sendOrder')}
          content={
            <ShoppingCartModalContent
              setOpenShoppingCart={setOpenShoppingCart}
              setProductList={setProductList}
              productsInShoppingCart={productsInShoppingCart}
              formhook={formhook}
              productsInCart={hook.selectedProductList}
            />
          }
          footerInfo={
            <ExtranetContainer
              twoThirds={minDesktop}
              full={!minDesktop}
              alignCenter
            >
              <PharmacCheckbox
                checked={orderConsent}
                handleChange={(event) => {
                  setOrderConsent(event.target.checked)
                }}
              >
                <PharmacText>
                  {t('extranet:shop.cart.acceptTerms')}
                  <PharmacLink
                    href={__ORDER_AND_DELIVERY_TERMS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('extranet:shop.cart.here')}
                  </PharmacLink>
                </PharmacText>
              </PharmacCheckbox>
            </ExtranetContainer>
          }
        />
      )}
      <EmptyShoppingCart
        style={{
          opacity: openEmptyShoppingCart ? 1 : 0,
          maxHeight: openEmptyShoppingCart ? '500px' : '38px',
          maxWidth: openEmptyShoppingCart ? '425px' : '38px',
          boxShadow: openEmptyShoppingCart ? '4px 4px 5px 0 rgba(0, 0, 0, 0.03)' : 'none',
          marginTop: minDesktop ? 'var(--spacing-small)' : 'var(--spacing-large)',
        }}
      >
        <EmptyShoppingCartInner>
          <ExtranetContainer
            full
            flexColumn
            paddingMedium
          >
            <ExtranetContainer
              full
              flexRow
              alignCenter
              spaceBetween
            >
              <PharmacH6 color="purple">{t('extranet:shop.cart.emptyCartTitle')}</PharmacH6>
              <ExtranetContainer
                style={{ cursor: 'pointer' }}
                onClick={() => setOpenEmptyShoppingCart(false)}
              >
                <PharmacIcon icon="x" />
              </ExtranetContainer>
            </ExtranetContainer>
            <PharmacText paddingMediumVertical>{t('extranet:shop.cart.emptyCartDesc')}</PharmacText>
            <ExtranetContainer
              full
              justifyEnd
              paddingSmallTop
            >
              <PharmacButton
                size="small"
                onClick={() => {
                  navigate(createLink('shop'))
                  setOpenEmptyShoppingCart(false)
                }}
              >
                <ExtranetContainer
                  full
                  marginSmallHorizontal
                >
                  <PharmacText marginSmallRight>
                    {t('extranet:shop.cart.emptyCartButton')}
                  </PharmacText>
                  <PharmacIcon
                    icon="arrowRight"
                    size="18px"
                  />
                </ExtranetContainer>
              </PharmacButton>
            </ExtranetContainer>
          </ExtranetContainer>
        </EmptyShoppingCartInner>
      </EmptyShoppingCart>
      {openEmptyShoppingCart && (
        <ShoppingCartClickLayer closeMenu={() => setOpenEmptyShoppingCart(false)} />
      )}
    </ShoppingCartRoot>
  )
}
